/** Which dashboard the chrome is rendering. */
export type Section = 'vendor' | 'admin';

type Input = {
  section: Section;
  /** Approved vendors from the id token. */
  vendors: readonly string[];
  currentVendor: string | null;
  isAdmin: boolean;
  name: string | null;
  email: string | null;
};

export type AccountMenuModel = {
  /** The acting vendor, or in the admin panel the person. */
  trigger: string;
  /** Real admin rights, not merely the admin section. */
  elevated: boolean;
  vendorLabel: string;
  vendors: readonly string[];
  /** The ticked vendor; `null` in the admin panel, where picking a vendor leaves the section. */
  selectedVendor: string | null;
  /** Only a Keboola admin sees the way into the admin panel. */
  switchToAdmin: boolean;
  profileTo: string;
};

/** Blank counts as absent, so a `''` token claim doesn't win the fallback chain. */
const readable = (value: string | null): string | null => (value?.trim() === '' ? null : value);

/**
 * The account menu for one dashboard section.
 *
 * - `vendor`: ticks and names `currentVendor`; offers `/admin` to admins.
 * - `admin`: names the person; vendor rows switch out of the section.
 */
export const buildAccountMenu = ({
  section,
  vendors,
  currentVendor,
  isAdmin,
  name,
  email,
}: Input): AccountMenuModel => {
  const who = readable(name) ?? readable(email) ?? 'Account';

  return section === 'admin'
    ? {
        trigger: who,
        // ⚠️ Gated on `isAdmin`: a non-admin sees this menu on `/admin`'s access-denied state.
        elevated: isAdmin,
        vendorLabel: 'Switch to vendor',
        vendors,
        selectedVendor: null,
        switchToAdmin: false,
        profileTo: isAdmin ? '/admin/profile' : '/~/profile',
      }
    : {
        trigger: currentVendor ?? who,
        elevated: false,
        vendorLabel: 'Vendor',
        vendors,
        selectedVendor: currentVendor,
        switchToAdmin: isAdmin,
        profileTo: '/~/profile',
      };
};
